import { IMenuItem, MenuModule, store, t, useMapState } from "@comact/crc";
import React from "react";
import CoachMark from "./UiSwitchCoachMark";
import { findPill, getPillLabelKey, hasSeenCoach, markCoachSeen, NARROW_QUERY, PULSE_DURATION_MS, setAttention } from "./coachMark";
import "./switchUi.css";
import { getSwitchToModernUrl, getUiSwitchInfo, IUiSwitchInfo, switchToModern } from "./uiSwitch";

export const SWITCH_UI_ITEM_ID = "header-switch-ui";
const COACH_POLL_MS = 250;
const COACH_POLL_TRIES = 40; // 10 s for the header to draw the pill, then give up for this page load

const useMediaQuery = (query: string) => {
    const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
    React.useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        const onChange = () => setMatches(mediaQuery.matches);
        mediaQuery.addEventListener("change", onChange);
        return () => mediaQuery.removeEventListener("change", onChange);
    }, [query]);
    return matches;
};

/**
 * The link to the new version, in the top-right of the header, drawn as an amber pill (switchUi.css) that is always labelled: the long label
 * on wide screens, a short one below 1100px, never an icon alone. It is a link that reloads the page, not a toggle.
 * The crc header has no slot for app content, so this renders nothing and registers a top-level menu item instead; it appears once the API says the new UI can be offered.
 * Where both UIs share an origin (production) the link goes through `/ui/switch`; in development it points straight at the
 * new UI's dev server.
 * The first time in a browser a coach-mark (UiSwitchCoachMark.tsx) introduces the pill; the pill then glows three times and stays.
 */
const SwitchUiHeaderItem = React.memo(() => {
    const pathname = useMapState((state) => state.routing.pathname) ?? "/"; // null until the router is ready
    const narrow = useMediaQuery(NARROW_QUERY);
    const [info, setInfo] = React.useState<IUiSwitchInfo | null>(null);
    const [coachOpen, setCoachOpen] = React.useState(false);
    const pulseTimer = React.useRef<number>(0);

    React.useEffect(() => {
        let mounted = true;
        getUiSwitchInfo().then((value) => { if (mounted) setInfo(value); });
        return () => { mounted = false; };
    }, []);

    React.useEffect(() => {
        if (!info?.modern) return undefined;
        const label = t(getPillLabelKey(narrow));
        store.dispatch(MenuModule.actions.updateOne({
            id: SWITCH_UI_ITEM_ID,
            name: getPillLabelKey(narrow),
            tooltip: "uiSwitch.tryNewTooltip",
            type: "switch",
            url: getSwitchToModernUrl(pathname, info),
            isService: true, // an absolute address, not under the app's base path
            immovable: true, // never folded into the overflow menu
            priority: 900, // after the other items, next to the hamburger
            className: "switchUiItem",
            "aria-label": `${label}. ${t("uiSwitch.tryNewTooltip")}`, // starts with the visible label
        } as IMenuItem));
        return () => {
            store.dispatch(MenuModule.actions.removeOne(SWITCH_UI_ITEM_ID));
        };
    }, [info, narrow, pathname]);

    // The coach-mark: once per browser, and only when the pill really is in the header (not on the sign-in, not when the header is absent).
    React.useEffect(() => {
        if (!info?.modern || hasSeenCoach()) return undefined;
        let tries = 0;

        const timer = window.setInterval(() => {
            tries += 1;
            if (findPill()) {
                window.clearInterval(timer);
                setAttention("spot");
                setCoachOpen(true);
            } else if (tries >= COACH_POLL_TRIES) {
                window.clearInterval(timer);
            }
        }, COACH_POLL_MS);
        return () => window.clearInterval(timer);
    }, [info]);

    React.useEffect(() => () => {
        window.clearTimeout(pulseTimer.current);
        setAttention(null);
    }, []);

    const dismissCoach = React.useCallback(() => {
        markCoachSeen();
        setCoachOpen(false);
        setAttention("pulse"); // three pulses, then the ring is removed
        window.clearTimeout(pulseTimer.current);
        pulseTimer.current = window.setTimeout(() => setAttention(null), PULSE_DURATION_MS);
    }, []);

    const tryFromCoach = React.useCallback(() => {
        markCoachSeen();
        setCoachOpen(false);
        setAttention(null);
        if (info) switchToModern(info, window.location.pathname);
    }, [info]);

    return coachOpen ? <CoachMark onTry={tryFromCoach} onDismiss={dismissCoach} /> : null;
});

/**
 * A shortcut in the header must never take the application down: whatever fails in it, it disappears and the page stays.
 * (A React error with no boundary above it unmounts the whole tree, which showed as a blank page.)
 */
class SwitchUiBoundary extends React.Component<React.PropsWithChildren, { failed: boolean; }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: unknown) {
        setAttention(null);
        console.warn("The link to the new version failed and was turned off for this page load.", error);
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

const SafeSwitchUiHeaderItem = () => (
    <SwitchUiBoundary>
        <SwitchUiHeaderItem />
    </SwitchUiBoundary>
);

export default SafeSwitchUiHeaderItem;
